import CloseIcon from '@mui/icons-material/Close'
import { Box, IconButton, Paper, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY_PREFIX = 'citiiq_feature_spotlight_dismissed'

interface NewFeatureSpotlightProps {
  /** Unique identifier for the feature (used in localStorage key) */
  readonly featureId: string
  /** The text shown to the user describing the new feature */
  readonly description: string
  /** CSS selector for the element to highlight (e.g. '#innovasium-chat-fab') */
  readonly targetSelector: string
  /** Current user's ID for per-user tracking */
  readonly userId: number | string
}

/**
 * A spotlight/tooltip component that draws attention to a target element
 * (e.g. the AI chat FAB) on first login. Shows only once per user.
 */
const NewFeatureSpotlight = ({
  featureId,
  description,
  targetSelector,
  userId,
}: NewFeatureSpotlightProps) => {
  const [visible, setVisible] = useState(false)
  const [backdropDismissable, setBackdropDismissable] = useState(false)
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null)

  const storageKey = `${STORAGE_KEY_PREFIX}_${featureId}_${userId}`

  const handleDismiss = useCallback(() => {
    localStorage.setItem(storageKey, 'true')
    setVisible(false)
  }, [storageKey])

  useEffect(() => {
    // Check if user has already dismissed this spotlight
    const dismissed = localStorage.getItem(storageKey)
    if (dismissed) return undefined

    /*
     * Re-measure the target so the highlight ring always wraps it perfectly,
     * even after the window is resized or scrolled (the chat FAB is fixed to
     * the viewport corner, so its position changes on resize).
     */
    const measureTarget = () => {
      const targetEl = document.querySelector(targetSelector)
      if (targetEl) {
        setTargetRect(targetEl.getBoundingClientRect())
      }
    }

    const resizeObserver = new ResizeObserver(measureTarget)

    // Wait a short delay for the target element to render
    const timer = setTimeout(() => {
      const targetEl = document.querySelector(targetSelector)
      if (!targetEl) return

      measureTarget()
      setVisible(true)

      window.addEventListener('resize', measureTarget)
      window.addEventListener('scroll', measureTarget, true)

      /*
       * Track size changes of the target element itself (e.g. the FAB
       * resizing on different breakpoints).
       */
      resizeObserver.observe(targetEl)
    }, 1500)

    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', measureTarget)
      window.removeEventListener('scroll', measureTarget, true)
      resizeObserver.disconnect()
    }
  }, [storageKey, targetSelector])

  // Auto-dismiss when the user clicks the target element (e.g. opens the chat)
  useEffect(() => {
    if (!visible) return undefined

    const targetEl = document.querySelector(targetSelector)
    if (!targetEl) return undefined

    const handleTargetClick = () => {
      handleDismiss()
    }

    targetEl.addEventListener('click', handleTargetClick)

    return () => {
      targetEl.removeEventListener('click', handleTargetClick)
    }
  }, [visible, targetSelector, handleDismiss])

  // Allow backdrop dismiss after a 1 second delay
  useEffect(() => {
    if (!visible) return undefined

    const timer = setTimeout(() => {
      setBackdropDismissable(true)
    }, 1000)

    return () => {
      clearTimeout(timer)
    }
  }, [visible])

  const handleBackdropClick = () => {
    if (backdropDismissable) {
      handleDismiss()
    }
  }

  if (!visible || !targetRect) return null

  return (
    <>
      {/* Backdrop overlay */}
      <Box
        onClick={handleBackdropClick}
        sx={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 2100,
          cursor: backdropDismissable ? 'pointer' : 'default',
        }}
      />

      {/* Highlight ring around the target element */}
      <Box
        sx={{
          position: 'fixed',
          top: targetRect.top - 8,
          left: targetRect.left - 8,
          width: targetRect.width + 16,
          height: targetRect.height + 16,
          borderRadius: '50%',
          border: '3px solid #9EC73D',
          boxShadow:
            '0 0 0 4px rgba(158, 199, 61, 0.3), 0 0 20px rgba(158, 199, 61, 0.4)',
          zIndex: 2400,
          pointerEvents: 'none',
          animation: 'spotlightPulse 2s ease-in-out infinite',
          '@keyframes spotlightPulse': {
            '0%, 100%': {
              boxShadow:
                '0 0 0 4px rgba(158, 199, 61, 0.3), 0 0 20px rgba(158, 199, 61, 0.4)',
            },
            '50%': {
              boxShadow:
                '0 0 0 8px rgba(158, 199, 61, 0.2), 0 0 30px rgba(158, 199, 61, 0.5)',
            },
          },
        }}
      />

      {/* Tooltip card */}
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: targetRect.height + 48,
          right: 24,
          maxWidth: 320,
          zIndex: 2400,
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#9EC73D',
            px: 2,
            py: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ color: '#1E1D1F', fontWeight: 700 }}
          >
            ✨ New Feature
          </Typography>
          <IconButton
            size="small"
            onClick={handleDismiss}
            aria-label="Dismiss"
            sx={{ color: '#1E1D1F' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ px: 2, py: 2, backgroundColor: '#2A2A2A' }}>
          <Typography variant="body2" sx={{ color: '#fff', lineHeight: 1.6 }}>
            {description}
          </Typography>
        </Box>

        {/* Arrow pointing down toward the FAB */}
        <Box
          sx={{
            position: 'absolute',
            bottom: -8,
            right: 38,
            width: 0,
            height: 0,
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderTop: '8px solid #2A2A2A',
          }}
        />
      </Paper>
    </>
  )
}

export default NewFeatureSpotlight
